/*
  The product catalogue: one entry per thing a customer can actually use.

  This replaced a single /features page carrying six stories. It is built
  from an inventory of the shipped code rather than from the old marketing
  copy, and the difference mattered: the old page never mentioned the
  citation score, the registry, the SuperSchema Score, verification, or
  monitor-only mode, and it described bot tracking as though it worked on
  its own when it needs a pixel installed and verified first.

  What is deliberately NOT in here, and why, because the next person to add
  a page will want to know:

  - Chief of Staff. Behind a gate that needs both a Growth tier and a
    manually enabled row, with a kill switch that fails safe. It writes to
    customers' live websites. It has no public documentation anywhere, and
    a private pilot is not a product page.
  - Domain-level `llms.txt` generation. The service and the route exist and
    nothing in the interface calls them; the knowledge base says in as many
    words that generation is on the roadmap. The scan CHECKS your llms.txt,
    which is real, and that is what `scan` claims. Nothing here says we
    write one for you.
  - REST API and CLI. Not built. The `batch_api` flag in the plan packages
    is read by no code at all.
  - The MCP server. It is mounted and live, and the knowledge base says it
    is not. That contradiction is a decision about whether to launch it,
    not a decision about how to word a page, so it stays off until somebody
    makes that call.

  Every `truth` line below is a constraint the page must not overstate, and
  several of them exist because the old page did.
*/
import { paidTier } from './hubspotEcosystem'

export interface ProductPoint {
  /** What it does, in the reader's words. */
  what: string
  /** How it does it, one sentence. */
  how: string
}

export interface Product {
  /** URL slug under /products. */
  slug: string
  /** The name in the menu. Short enough to sit in a column. */
  name: string
  /** One line under the name in the menu. */
  blurb: string
  /** Which menu group it sits in. */
  group: 'see' | 'fix' | 'keep'
  /** The page's headline. A sentence, never a label. */
  headline: string
  /** The page's opening paragraph. */
  lead: string
  /**
   * Why this matters, before what it does.
   *
   * The stakes, in the customer's terms: what is happening to them right
   * now whether or not they buy anything. A product page that opens on
   * capabilities asks a reader to work out for themselves why they should
   * care, and most of them will not do that work.
   */
  why: string[]
  /** What is different afterwards. One sentence, and it is the promise. */
  outcome: string
  /** The three or four things this product actually does. */
  points: ProductPoint[]
  /**
   * What this does not do, or what it needs from the customer first.
   *
   * Not a disclaimer at the bottom of the page: it is on the page because a
   * product page that only says yes is the reason people arrive at support
   * surprised.
   */
  truth: string
  /** Which plans have it, in plain words. Taken from the plan packages. */
  plans: string
  /** Where to go next. */
  cta: { label: string; href: string }
  /**
   * A guide that explains the subject rather than the product, linked from
   * under "Why this matters". The product page keeps the pitch; the guide
   * does the teaching.
   */
  guide?: { label: string; href: string }
}

export const productGroups: { id: Product['group']; title: string; blurb: string }[] = [
  {
    id: 'see',
    title: 'See what AI understands',
    blurb: 'Find out what AI systems can read about your business today.',
  },
  {
    id: 'fix',
    title: 'Fix what it cannot read',
    blurb: 'Give AI the facts, in a form it can parse, on your own pages.',
  },
  {
    id: 'keep',
    title: 'Keep it true',
    blurb: 'Websites change. This is the part that notices.',
  },
]

export const products: Product[] = [
  /* ---------------- See ---------------- */
  {
    slug: 'scan',
    name: 'AI Readiness Scan',
    blurb: 'What AI can read about your site today, scored out of 100.',
    group: 'see',
    headline: 'Find out what AI systems can actually understand about your site.',
    lead:
      'Enter your address and SuperSchema reads your site the way an AI system does: what it can reach, what it can parse, and what it can verify. You get a SuperSchema Score out of 100 and the pages holding it back.',
    why: [
      'A buyer asks an assistant about your category and gets three names back. Whether you are one of them was decided long before they asked, by what a crawler could read on your site months ago.',
      'Most sites fail this in ways nobody on the team can see. The page looks right in a browser, and the machine reading it gets a navigation menu, a cookie banner, and no clear statement of what the business does.',
      'You cannot fix what you have not measured, and nobody is going to tell you. There is no Search Console for this.',
    ],
    outcome:
      'You stop guessing. You know what AI can read about you today, which pages are dragging you down, and what to do first.',
    points: [
      {
        what: 'A score you can act on',
        how: 'Five categories make up the SuperSchema Score: access, content, structure, schema, and AI-readable files. Each one names the pages that cost you points.',
      },
      {
        what: 'Page by page, not just site-wide',
        how: 'Every page gets its own readability breakdown, so a single thin template does not hide behind a healthy average.',
      },
      {
        what: 'Your llms.txt, checked',
        how: 'We fetch it, grade it, and tell you whether it is healthy, needs work, broken, or unsafe.',
      },
      {
        what: 'Rescan whenever you want',
        how: 'Run it again after a change and compare two scans side by side to see what moved.',
      },
    ],
    truth:
      'The scan checks your llms.txt. It does not write one for you: that is on the roadmap, not in the product.',
    plans: 'The first scan is free with a free account. Repeat scans and history come with every paid plan.',
    cta: { label: 'Start free', href: '/sign-up' },
  },
  {
    slug: 'ai-recommendations',
    name: 'AI Recommendations',
    blurb: 'Whether ChatGPT, Claude, and Gemini actually name you.',
    group: 'see',
    headline: 'Find out whether AI assistants recommend you, or somebody else.',
    lead:
      'Being readable is not the same as being recommended. SuperSchema asks ChatGPT, Claude, and Gemini the questions your buyers ask, reads what comes back, and records whether you were recommended, mentioned in passing, or not named at all.',
    why: [
      'Being readable is table stakes. Being recommended is the thing that puts a buyer in front of you, and the two are not the same: an assistant can understand your site perfectly and still name three competitors.',
      'This is now a real acquisition channel and almost nobody measures it. Teams track rankings they can no longer influence and have no idea what an assistant says when a buyer asks who to use.',
      'It moves. Models update, competitors publish, your own pages change. A read taken once is a read that is already out of date.',
    ],
    outcome:
      'You know what the assistants actually say when somebody asks about your category, and whether the work you are doing is changing it.',
    points: [
      {
        what: 'Real questions, real answers',
        how: 'We run the buyer questions for your category and keep what each assistant said, so the result is evidence rather than an estimate.',
      },
      {
        what: 'Three assistants, not one',
        how: 'ChatGPT, Claude, and Gemini are asked separately, because they do not answer the same way and one of them is not a sample.',
      },
      {
        what: 'Tracked over time',
        how: 'Rescan and the answers are kept, so you can see whether the work you did changed what the assistants say.',
      },
    ],
    truth:
      'Nobody can promise a ranking inside an AI system, and this does not. It measures what the assistants say about you, so you can tell whether it is changing.',
    plans: 'Two scans a month on Essential, five on Growth, twenty on Portfolio, unlimited on Enterprise.',
    cta: { label: 'See plans', href: '/pricing' },
  },
  {
    slug: 'bot-tracking',
    name: 'AI Bot Tracking',
    blurb: 'Which AI crawlers reach your pages, and how often.',
    group: 'see',
    headline: 'See which AI crawlers are reading you, and which pages they take.',
    lead:
      'GPTBot, ClaudeBot, PerplexityBot, Google-Extended and the rest leave a trail. SuperSchema records those visits and shows you which bots arrive, how often, and which of your pages they actually read.',
    why: [
      'AI crawlers are reading your site right now, or they are not, and there is nothing in your analytics that tells you which. They do not run JavaScript, so they never show up in the tools you already have.',
      'A robots.txt line, a firewall rule, or a bot-protection service can block them all without anybody noticing. Sites lose this channel for months at a time and find out by accident.',
      'The pages they take are usually not the pages you optimised. Knowing which ones actually get read is what tells you where the work is worth doing.',
    ],
    outcome:
      'You can see the crawlers arriving, which pages they take, and the day something stops them.',
    points: [
      {
        what: 'Every bot, named',
        how: 'A breakdown by crawler, so you can tell whether the systems you care about are reaching you at all.',
      },
      {
        what: 'Your most-read pages',
        how: 'Which URLs the crawlers take most, which is usually not the list you would have guessed.',
      },
      {
        what: 'A daily timeline',
        how: 'Visits over time, so a block, a robots.txt change, or a new deployment shows up as a shape rather than a surprise.',
      },
    ],
    truth:
      'This needs a small tracking pixel installed on your site and verified before it records anything. It is not a passive report we can run on your behalf.',
    plans: 'Pro plans only. One domain on Essential, three on Growth, ten on Portfolio, unlimited on Enterprise.',
    cta: { label: 'See plans', href: '/pricing' },
    guide: { label: 'What AI bot visits can tell you', href: '/hubspot-aeo/ai-bot-tracking' },
  },

  /* ---------------- Fix ---------------- */
  {
    slug: 'schema',
    name: 'Schema Generation',
    blurb: 'Valid JSON-LD for every page, written by reading the page.',
    group: 'fix',
    headline: 'Schema that describes the page you actually have.',
    lead:
      'SuperSchema reads a page, works out what it is, and writes the JSON-LD for it. Not a template with your name dropped in: the types are chosen from the content, and a page that is three things gets all three.',
    why: [
      'A page tells a person what it is through layout, images, and context. A machine gets none of that. Without structured data it is guessing from text, and a guess is how a business ends up described as something it does not do.',
      'Schema is also how you state facts a machine can rely on: what you sell, who it is for, what it costs, how to start. Left unstated, those get inferred, and an inference presented confidently is indistinguishable from a fact.',
      'Doing it by hand does not survive contact with a real site. It is fine for five pages and impossible for five hundred, which is why most sites have schema on the homepage and nothing anywhere else.',
    ],
    outcome:
      'Every page states what it is and what you offer, in a form a machine can read without guessing.',
    points: [
      {
        what: 'Twenty-seven types, picked for you',
        how: 'Article, FAQPage, HowTo, LocalBusiness, Organization, Product, Event, Recipe, Review, Service, Course, JobPosting, VideoObject and more. Leave it on Auto and the page decides.',
      },
      {
        what: 'Scored, then improved',
        how: 'Every schema gets a quality score for completeness and depth, and two AI refinement passes to raise it before you deploy anything.',
      },
      {
        what: 'Editable and validated',
        how: 'Change any field by hand and validate it against the spec before it goes near your site.',
      },
      {
        what: 'A Google rich-results preview',
        how: 'See the listing your markup would produce, and which types are eligible for a rich result.',
      },
    ],
    truth:
      'We detect schema already on your page and tell you, so nothing you wrote gets quietly overwritten.',
    plans: 'Your first page is free with a free account, on a paid HubSpot plan. Unlimited generation on every paid plan.',
    cta: { label: 'Try the free grader', href: '/schema-markup-grader' },
  },
  {
    slug: 'ai-readable-files',
    name: 'AI-Readable Files',
    blurb: 'A clean Markdown and text copy of every page, hosted.',
    group: 'fix',
    headline: 'Give AI a clean copy of the page, without touching your design.',
    lead:
      'Your pages are built for people: navigation, banners, cookie notices, a footer with forty links. SuperSchema extracts what the page actually says, writes it as Markdown and plain text, hosts both, and gives you two link tags for the head.',
    why: [
      'Your pages are built for people. Navigation, banners, a cookie notice, a footer with forty links: a machine reading that has to find the actual content inside it, and often does not.',
      'The systems that matter here mostly do not run JavaScript. If your content arrives after the page loads, a crawler can come away with an empty page and no error to tell anybody.',
      'A clean copy costs you nothing in design. The page a person sees does not change at all.',
    ],
    outcome:
      'AI systems get the words your page actually says, without the furniture around them.',
    points: [
      {
        what: 'The content, not the chrome',
        how: 'The extractor takes the real body of the page and leaves the navigation, the widgets, and the boilerplate behind.',
      },
      {
        what: 'Two formats, both hosted',
        how: 'A Markdown copy and a plain-text copy, served from our CDN, so there is nothing to build or deploy on your side.',
      },
      {
        what: 'Two tags in the head',
        how: 'A link rel="alternate" for each. That is the whole install.',
      },
    ],
    truth:
      'These are per-page files. A site-level llms.txt is a different thing, and we check yours rather than write it.',
    plans: 'Included on Essential and above.',
    cta: { label: 'See plans', href: '/pricing' },
  },
  {
    slug: 'deploy',
    name: 'Deploy and Verify',
    blurb: 'Get it onto the live page, then prove it is there.',
    group: 'fix',
    headline: 'Getting it live is the part that usually stalls.',
    lead:
      'Schema that sits in a dashboard does nothing. SuperSchema pushes it onto the page for you where we can, hands you the exact block where we cannot, and then fetches the live page to confirm it actually arrived.',
    why: [
      'This is where the work usually stops. Schema is generated, everybody agrees it is right, and then it sits in a dashboard because getting it onto the site means a ticket, a sprint, and a developer who has other things to do.',
      'Deployed and live are different things. A CMS can accept a change and not publish it, a cache can serve the old page for a day, and a template change can quietly drop the markup on the way out.',
      'Nobody checks. Teams mark the task done at the point they pressed save, which is the point before the thing that could go wrong goes wrong.',
    ],
    outcome:
      'The schema is on the live URL, and you have seen it there rather than assumed it.',
    points: [
      {
        what: 'HubSpot, done for you',
        how: 'Connect HubSpot and push schema and AI-readable link tags straight into blog posts, pages, landing pages, and knowledge articles.',
      },
      {
        what: 'Anywhere else, one paste',
        how: 'An install package with the exact block for your head. Copy it once and you are done.',
      },
      {
        what: 'Verified against the live page',
        how: 'We fetch the page as a crawler would and confirm the schema and the files are really there. Not "deployed" in a database: present, on the URL, now.',
      },
    ],
    truth:
      'Verification reads your live page, so a change that has not published yet will read as missing until it does.',
    plans: `Copy-paste and verification are on every plan, including the free trial. ${paidTier.full}`,
    cta: { label: 'See plans', href: '/pricing' },
  },

  /* ---------------- Keep ---------------- */
  {
    slug: 'monitoring',
    name: 'Monitoring',
    blurb: 'We check every night and tell you when a page drifts.',
    group: 'keep',
    headline: 'Websites change. Schema written six months ago quietly stops being true.',
    lead:
      'Somebody edits a price. A page gets rebuilt. A template changes and the markup goes with it. SuperSchema keeps a baseline of every verified page, re-checks on a schedule, and raises a drift event when the page and its schema stop agreeing.',
    why: [
      'Schema goes stale quietly. Somebody edits a price, a page gets rebuilt, a template changes, and the markup underneath stops matching the page. Nothing breaks and no one is told.',
      'Around one page in ten drifts within six months. The failure is invisible from the front end, because the page still looks right: it is only wrong to the machines reading it.',
      'A wrong fact stated confidently is worse than a missing one. An assistant quoting a price you stopped charging is not a gap in coverage, it is a customer arriving with the wrong expectation.',
    ],
    outcome:
      'You hear about a page the night it drifts, instead of finding out from a customer months later.',
    points: [
      {
        what: 'A baseline, then a comparison',
        how: 'We record what the page said when it was verified and compare against it, so drift is a measured difference rather than a hunch.',
      },
      {
        what: 'Drift you can resolve',
        how: 'Each event says what changed and gives you the choice: put it back, or accept the new version as the baseline.',
      },
      {
        what: 'You choose which pages',
        how: 'Your plan buys a number of monitored pages and you decide which URLs occupy the slots.',
      },
    ],
    truth:
      'A page that is gone is reported as gone, not as broken. The two are different problems and they are not run together.',
    plans: 'Weekly checks on Essential and Growth, daily on Portfolio and Enterprise. Fifty monitored pages up to unlimited.',
    cta: { label: 'See plans', href: '/pricing' },
  },
  {
    slug: 'monitor-only',
    name: 'Monitor-Only Mode',
    blurb: 'Watch a page without changing anything on it.',
    group: 'keep',
    headline: 'Watch a page without letting us touch it.',
    lead:
      'Some pages you do not want anybody writing to, including us. Monitor-only mode watches a page’s own existing schema and tells you when it changes, while SuperSchema generates nothing for it and deploys nothing to it.',
    why: [
      'Not every page is yours to change. Some are under legal review, some belong to another team, some are on a platform where an automated edit is simply not allowed.',
      'The usual answer is to leave those pages out entirely, which means the pages that matter most are the ones nobody is watching.',
      'Watching and writing are separable, and treating them as one thing is a tooling assumption rather than a real constraint.',
    ],
    outcome:
      'The pages you cannot let anything write to are still watched, and you still hear when they change.',
    points: [
      {
        what: 'Your schema, not ours',
        how: 'We take a baseline of what is already on the page and watch that, whoever wrote it.',
      },
      {
        what: 'Nothing written back',
        how: 'No generation, no deployment, no edits. The page is observed and left alone.',
      },
      {
        what: 'The same drift reporting',
        how: 'When the page and its markup stop agreeing, you hear about it in the same place as everything else.',
      },
    ],
    truth:
      'This is the mode for pages under someone else’s control, or under review. It reports, and that is all it does.',
    plans: 'On every paid plan, as part of monitoring.',
    cta: { label: 'See plans', href: '/pricing' },
  },
  {
    slug: 'registry',
    name: 'SuperSchema Registry',
    blurb: 'A public, AI-readable listing of your business facts.',
    group: 'keep',
    headline: 'A clean, machine-readable page about your business, hosted by us.',
    lead:
      'Your own site is the source, but it is built for people first. A registry listing at superschema.ai gives AI systems a structured, unambiguous place to read who you are, what you offer, and which of your pages matter.',
    why: [
      'Your own site is the source of truth, and it is also the hardest place to read: built for people, wrapped in design, and different on every page.',
      'AI systems reward a clean, unambiguous statement of who you are. Most businesses do not have one anywhere, which leaves the answer to be assembled from whatever a model happens to find.',
      'A structured listing is a second place to be right, in a form built for parsing rather than browsing, and it is one you control.',
    ],
    outcome:
      'There is one clean, machine-readable statement of your business facts, and you decide what is in it.',
    points: [
      {
        what: 'Structured, not styled',
        how: 'Your business facts, your schema coverage, and your important pages, in a form built to be parsed rather than browsed.',
      },
      {
        what: 'Readable three ways',
        how: 'The listing is served as a page, as Markdown, and as plain text, so a system that does not run JavaScript still gets everything.',
      },
      {
        what: 'In the public index',
        how: 'Listings are in a public sitemap that search engines are pinged about, so they can be found rather than only fetched.',
      },
    ],
    truth:
      'The listing is public by design. It carries what you have chosen to publish about your business and nothing else.',
    plans: 'Growth and above.',
    cta: { label: 'See plans', href: '/pricing' },
  },
]

/* Things that already have a page. The menu links to them rather than a
   second page competing with the one that exists. */
export const productMenuExtras = [
  { label: 'SuperSchema Audit', href: '/audit', blurb: 'A done-for-you diagnostic, delivered by our team.' },
  { label: 'Schema Markup Grader', href: '/schema-markup-grader', blurb: 'Check any URL, free, no account.' },
  { label: 'Methodology', href: '/methodology', blurb: 'How the audit measures what it measures.' },
  { label: 'Help centre', href: '/docs', blurb: 'Seventy articles on every part of the product.' },
]

export const byGroup = (group: Product['group']) => products.filter((p) => p.group === group)

/*
  The sentence a product page adds to its closing when the product needs a
  paid SuperSchema plan, or null when it does not.

  Every product page closes on the free account, under the one line that
  ends "It needs a paid HubSpot plan." On a page that has just said the
  product needs a plan, that sentence read as though it were about the
  product, and nothing said the free account does not include it.

  The rule is the page's own: a product whose first button goes to plans.
  The scan, the schema and the deploy are what the free account's first
  page is made of, so they never take the sentence. For two of them the
  first button already goes elsewhere. Deploy and Verify is the one the
  list is needed for: its first button goes to plans, because every page
  after the first comes with one, but saying the product comes with a paid
  plan under "your first page fixed free" would contradict the line above.
*/
const IN_THE_FIRST_FREE_PAGE = ['scan', 'schema', 'deploy']

export const paidPlanLine = (product: Product): string | null =>
  product.cta.href === '/pricing' && !IN_THE_FIRST_FREE_PAGE.includes(product.slug)
    ? `${product.name} comes with a paid SuperSchema plan.`
    : null
